/**
 * WishComposerModal — write & send a wish for a chosen occasion.
 * Saves permanently to the member's life timeline via
 * POST /family-trees/{treeId}/wishes; offers a WhatsApp share afterwards.
 */
import { useMemo, useRef, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Send, Lock, Users as UsersIcon, Mic, Square, Trash2 } from "lucide-react";

export default function WishComposerModal({ treeId, members = [], member, occasion, onClose, onSent }) {
  const [memberId, setMemberId] = useState(member?.id || members[0]?.id || "");
  const [message, setMessage] = useState("");
  const [visibility, setVisibility] = useState(occasion?.private_default ? "private" : "family");
  const [busy, setBusy] = useState(false);
  const [sentWish, setSentWish] = useState(null);
  const [isGroupCard, setIsGroupCard] = useState(false);
  const [deliverOn, setDeliverOn] = useState("");
  const [audioDataUrl, setAudioDataUrl] = useState("");
  const [recState, setRecState] = useState("idle"); // idle | recording | done
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size > 6 * 1024 * 1024) {
          toast.error("Recording too long — please keep it under ~2 minutes.");
          setRecState("idle");
          return;
        }
        const r = new FileReader();
        r.onload = () => { setAudioDataUrl(r.result); setRecState("done"); };
        r.readAsDataURL(blob);
      };
      rec.start();
      recRef.current = rec;
      setRecState("recording");
    } catch {
      toast.error("Microphone access was denied — please allow it to record a voice greeting.");
    }
  };
  const stopRec = () => recRef.current?.stop();

  const target = useMemo(
    () => member || members.find((m) => m.id === memberId),
    [member, members, memberId]
  );
  const targetName = target ? [target.first_name, target.last_name].filter(Boolean).join(" ") : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!memberId) return toast.error("Choose a family member first.");
    if (message.trim().length < 3 && !audioDataUrl) return toast.error("Write a short message or record a voice greeting.");
    setBusy(true);
    try {
      const { data } = await http.post(`/family-trees/${treeId}/wishes`, {
        member_id: memberId,
        category_key: occasion?.category_key || "birthdays",
        occasion_label: occasion?.label || "Wish",
        message: message.trim(),
        visibility,
        is_group_card: isGroupCard,
        deliver_on: deliverOn || undefined,
        audio_data_url: audioDataUrl || undefined,
      });
      setSentWish(data);
      toast.success(deliverOn ? `Scheduled for ${deliverOn}.` : visibility === "private" ? "Private wish sent." : "Posted on the family board!");
      onSent?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send your wish.");
    } finally {
      setBusy(false);
    }
  };

  const waShare = () => {
    const text = `🎉 ${occasion?.label || "A wish"} for ${targetName}:\n\n${message.trim()}\n\n— via IndianFamilyTree.com`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  return (
    <div className="fixed inset-0 z-[130] bg-[#14213D]/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="wofy-card w-full max-w-lg text-left max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="wish-composer-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="overline" style={{ color: "#B25D38" }}>SEND A WISH</div>
            <h3 className="font-display text-xl mt-0.5" data-testid="wish-composer-occasion">{occasion?.label || "A wish"}</h3>
            {targetName && <div className="text-xs opacity-70 mt-0.5">For <b>{targetName}</b></div>}
          </div>
          <button type="button" onClick={onClose} className="p-1 opacity-60 hover:opacity-100" data-testid="wish-composer-close"><X className="w-5 h-5" /></button>
        </div>

        {!sentWish ? (
          <>
            {!member && members.length > 0 && (
              <div className="mb-3">
                <label className="text-xs font-medium mb-1 block">Family member</label>
                <select className="wofy-input" value={memberId} onChange={(e) => setMemberId(e.target.value)} data-testid="wish-member-select">
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>{[m.first_name, m.last_name].filter(Boolean).join(" ")}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="mb-3">
              <label className="text-xs font-medium mb-1 block">Your message</label>
              <textarea
                dir="auto"
                className="wofy-input"
                rows={5}
                maxLength={4000}
                placeholder={`Write your ${occasion?.label?.toLowerCase() || "wish"} from the heart…`}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="wish-message-input"
              />
            </div>
            <div className="mb-3">
              <label className="text-xs font-medium mb-1 block">Voice greeting (optional)</label>
              {recState === "idle" && (
                <button type="button" onClick={startRec} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold" style={{ borderColor: "var(--wofy-border)" }} data-testid="wish-record-btn">
                  <Mic className="w-3.5 h-3.5" style={{ color: "#DC2626" }} /> Record a spoken blessing
                </button>
              )}
              {recState === "recording" && (
                <button type="button" onClick={stopRec} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-white animate-pulse" style={{ background: "#DC2626" }} data-testid="wish-record-stop">
                  <Square className="w-3.5 h-3.5" /> Recording… tap to stop
                </button>
              )}
              {recState === "done" && audioDataUrl && (
                <div className="flex items-center gap-2" data-testid="wish-audio-preview">
                  <audio controls src={audioDataUrl} className="h-9 flex-1" />
                  <button type="button" onClick={() => { setAudioDataUrl(""); setRecState("idle"); }} className="opacity-60 hover:opacity-100" data-testid="wish-audio-remove">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium mb-1 block">Schedule delivery (optional)</label>
                <input
                  type="date"
                  className="wofy-input"
                  min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                  value={deliverOn}
                  onChange={(e) => setDeliverOn(e.target.value)}
                  data-testid="wish-deliver-on"
                />
                <div className="text-[10px] opacity-60 mt-0.5">Stays hidden until this date, then appears automatically.</div>
              </div>
              <div className="flex items-start pt-5">
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="wish-group-card-label">
                  <input type="checkbox" checked={isGroupCard} onChange={(e) => setIsGroupCard(e.target.checked)} data-testid="wish-group-card-checkbox" />
                  <span><b>Group card</b> — invite relatives to co-sign this as one family keepsake</span>
                </label>
              </div>
            </div>
            <div className="mb-3">
              <label className="text-xs font-medium mb-1 block">Who can see it?</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setVisibility("family")} data-testid="wish-visibility-family"
                  className="flex-1 rounded-xl border px-3 py-2 text-xs font-semibold inline-flex items-center justify-center gap-1.5"
                  style={visibility === "family" ? { borderColor: "#B25D38", background: "#FFF7ED", color: "#B25D38" } : { borderColor: "var(--wofy-border)" }}>
                  <UsersIcon className="w-3.5 h-3.5" /> Family board
                </button>
                <button type="button" onClick={() => setVisibility("private")} data-testid="wish-visibility-private"
                  className="flex-1 rounded-xl border px-3 py-2 text-xs font-semibold inline-flex items-center justify-center gap-1.5"
                  style={visibility === "private" ? { borderColor: "#B25D38", background: "#FFF7ED", color: "#B25D38" } : { borderColor: "var(--wofy-border)" }}>
                  <Lock className="w-3.5 h-3.5" /> Private wish
                </button>
              </div>
              <div className="text-[11px] opacity-60 mt-1">
                {visibility === "family"
                  ? "Visible to everyone who can view this family tree — saved to their life timeline."
                  : "Only you, the recipient and tree admins can see this."}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
              <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="wish-submit">
                {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : (<><Send className="w-4 h-4" /> Send wish</>)}
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-4" data-testid="wish-sent-confirmation">
            <div className="text-3xl mb-2">💌</div>
            <div className="font-display text-lg">
              {sentWish.deliver_on && sentWish.deliver_on > new Date().toISOString().slice(0, 10)
                ? `Scheduled — your wish will appear on ${targetName}'s timeline on ${sentWish.deliver_on}.`
                : `Your wish has been saved to ${targetName}'s timeline.`}
            </div>
            {sentWish.is_group_card && (
              <div className="text-xs opacity-70 mt-1">It's a group card — relatives can now co-sign it from {targetName}'s profile.</div>
            )}
            <div className="mt-4 flex justify-center gap-2">
              <button type="button" onClick={waShare} className="rounded-full px-4 py-2 text-xs font-semibold text-white" style={{ background: "#25D366" }} data-testid="wish-whatsapp-share">
                Share on WhatsApp
              </button>
              <button type="button" onClick={onClose} className="wofy-btn-ghost text-xs">Done</button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
